<template>
  <!-- Add animated background -->
  <div class="animated-background">
    <div class="floating-elements">
      <span class="element code">{ }</span>
      <span class="element book">📚</span>
      <span class="element graduation">🎓</span>
      <span class="element tech">💻</span>
      <span class="element atom">⚛️</span>
      <span class="element formula">∑</span>
      <span class="element infinity">∞</span>
      <span class="element binary">01</span>
      <span class="element circuit"></span>
      <span class="element grid"></span>
    </div>
  </div>
  <div dir="rtl">
    <!-- First row: Page title -->
    <div class="mb-4">
      <h1>حجز دورة</h1>
    </div>
    
    <!-- Second row: Add button and search bar -->
    <div class="d-flex mb-4">
      <div style="width: 25%; padding-left: 16px;">
        <v-btn 
          color="primary" 
          @click="showAddDialog = true" 
          block
          height="56px"
          class="mt-n1"
        >
          <v-icon start>mdi-plus</v-icon>
          حجز دورة
        </v-btn>
      </div>
      <div style="width: 75%;">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="بحث"
          single-line
          hide-details
          class="rtl-input"
        ></v-text-field>
      </div>
    </div>

    <!-- Main table -->
    <v-data-table
      :headers="headers"
      :items="bookings"
      :search="search"
      :loading="loading"
      :items-per-page="10"
      :sort-by="[{key: 'id', order: 'desc'}]"
      class="elevation-1 rtl-table main-table"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item.customer }}</td>
          <td>{{ item.course?.name || '' }}</td>
          <td>{{ formatDateRange(item.course?.date_from, item.course?.date_to) }}</td>
          <td>{{ formatTimeRange(item.course?.time_from, item.course?.time_to) }}</td>
          <td>{{ item.number_of_people || 1 }}</td>
          <td>{{ formatNumber(item.total_price) }} د.ل</td>
          <td>{{ formatNumber(item.discount) }} د.ل</td>
          <td>{{ formatNumber(item.net_price) }} د.ل</td>
          <td>
            <div :style="getRemainingCellStyle(item.remaining)">
              {{ formatNumber(item.remaining) }} د.ل
            </div>
          </td>
          <td>
            <v-chip
              :color="getStatusColor(item.status)"
              text-color="white"
              size="small"
            >
              {{ getStatusText(item.status) }}
            </v-chip>
          </td>
          <td class="text-center">
            <v-icon small class="mr-2" @click="editItem(item)">
              mdi-pencil
            </v-icon>
            <v-icon 
              small 
              class="mr-2" 
              @click="confirmDeleteBooking(item.id)" 
              v-if="hasDeletePermission"
              color="error"
              title="حذف الحجز"
            >
              mdi-delete
            </v-icon>
            <v-icon 
              small 
              class="mr-2"
              @click="showPayments(item)" 
              title="المدفوعات"
            >
              mdi-cash-multiple
            </v-icon>
            <v-icon 
              small 
              class="mr-2" 
              @click="printBookingReceipt(item)" 
              title="طباعة إيصال الحجز" 
              color="primary"
            >
              mdi-printer
            </v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Payments Dialog -->
    <v-dialog v-model="paymentsDialog" max-width="900px">
      <v-card class="rtl-dialog payment-dialog">
        <v-card-title class="text-h5">المدفوعات</v-card-title>
        <v-card-text>
          <!-- Booking Summary -->
          <v-row class="mb-4 summary-row">
            <v-col cols="12" md="3">
              <strong>العميل:</strong> {{ selectedBooking.customer }}
            </v-col>
            <v-col cols="12" md="3">
              <strong>الدورة:</strong> {{ selectedBooking.course_name }}
            </v-col>
            <v-col cols="12" md="3">
              <strong>المبلغ الإجمالي:</strong> {{ formatNumber(selectedBooking.net_price) }} د.ل
            </v-col>
            <v-col cols="12" md="3">
              <strong>المتبقي:</strong> {{ formatNumber(Number(selectedBooking.remaining)) }} د.ل
            </v-col>
          </v-row>

          <!-- Add Payment Button -->
          <v-row class="mb-4">
            <v-col cols="12" class="text-left">
              <v-btn
                color="primary"
                @click="showAddPayment"
                :disabled="!Number(selectedBooking.remaining)"
              >
                <v-icon start>mdi-plus</v-icon>
                إضافة دفعة
              </v-btn>
            </v-col>
          </v-row>

          <!-- Payments Table -->
          <v-data-table
            :headers="paymentHeaders"
            :items="payments"
            class="elevation-1"
          >
            <template #[`item.created_at`]="{ item }">
              {{ formatDate(item.created_at) }}
            </template>
            <template #[`item.amount`]="{ item }">
              {{ formatNumber(item.amount) }} د.ل
            </template>
            <template #[`item.remaining_before`]="{ item }">
              {{ formatNumber(item.remaining_before) }} د.ل
            </template>
            <template #[`item.remaining_after`]="{ item }">
              {{ formatNumber(item.remaining_after) }} د.ل
            </template>
            <template #[`item.actions`]="{ item: payment }">
              <v-icon
                small
                class="mr-2"
                @click="printReceipt(payment)"
                title="طباعة الإيصال"
              >
                mdi-printer
              </v-icon>
              <v-icon
                small
                @click="confirmDeletePayment(payment)"
                v-if="hasDeletePermission && payment.id"
                color="error"
                title="حذف الدفعة"
              >
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="paymentsDialog = false">
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Payment Dialog -->
    <v-dialog v-model="addPaymentDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">إضافة دفعة جديدة</v-card-title>
        <v-card-text>
          <v-form ref="paymentForm">
            <v-text-field
              v-model="newPayment.amount"
              label="المبلغ"
              type="number"
              :rules="[
                v => !!v || 'المبلغ مطلوب',
                v => Number(v) <= Number(selectedBooking.remaining) || 'المبلغ أكبر من المتبقي'
              ]"
              class="rtl-input"
            ></v-text-field>
            <v-textarea
              v-model="newPayment.note"
              label="ملاحظات"
              rows="3"
              class="rtl-input"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="addPaymentDialog = false">
            إلغاء
          </v-btn>
          <v-btn color="blue-darken-1" variant="text" @click="savePayment">
            حفظ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Payment Confirmation Dialog -->
    <v-dialog v-model="showDeletePaymentDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">حذف الدفعة</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف هذه الدفعة؟
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="showDeletePaymentDialog = false">
            إلغاء
          </v-btn>
          <v-btn color="red-darken-1" variant="text" @click="deletePaymentConfirmed">
            حذف
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف هذا الحجز؟
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="showDeleteDialog = false">
            إلغاء
          </v-btn>
          <v-btn color="red-darken-1" variant="text" @click="confirmDelete">
            حذف
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showAddDialog" max-width="800px">
      <v-card class="rtl-dialog booking-dialog">
        <v-card-title class="text-h5">
          {{ editedItem.id ? 'تعديل حجز' : 'حجز دورة جديدة' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.customer"
                  label="اسم العميل"
                  :rules="[v => !!v || 'اسم العميل مطلوب']"
                  class="rtl-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.course_id"
                  :items="courses"
                  item-title="name"
                  item-value="id"
                  label="الدورة"
                  :rules="[v => !!v || 'الدورة مطلوبة']"
                  class="rtl-input"
                  @update:model-value="updateCourseSelection"
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="selectedCoursePrice"
                  label="سعر الشخص الواحد"
                  type="number"
                  readonly
                  class="rtl-input"
                  suffix="د.ل"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editedItem.number_of_people"
                  label="عدد الأشخاص"
                  type="number"
                  :rules="[
                    v => !!v || 'عدد الأشخاص مطلوب',
                    v => v > 0 || 'يجب أن يكون عدد الأشخاص أكبر من صفر',
                    v => Number.isInteger(Number(v)) || 'يجب أن يكون عدد الأشخاص عددًا صحيحًا'
                  ]"
                  class="rtl-input"
                  @input="calculateTotalPrice"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editedItem.total_price"
                  label="المبلغ الإجمالي"
                  type="number"
                  readonly
                  :rules="[v => !!v || 'المبلغ الإجمالي مطلوب']"
                  class="rtl-input"
                  suffix="د.ل"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.discount"
                  label="الخصم"
                  type="number"
                  class="rtl-input"
                  suffix="د.ل"
                  @input="calculateNetAmount"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="netAmount"
                  label="المبلغ الصافي"
                  type="number"
                  readonly
                  class="rtl-input"
                  suffix="د.ل"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.payment_type"
                  :items="paymentTypes"
                  item-title="text"
                  item-value="value"
                  label="طريقة الدفع"
                  :rules="[v => !!v || 'طريقة الدفع مطلوبة']"
                  required
                  class="rtl-input"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.status"
                  :items="statusOptions"
                  label="الحالة"
                  class="rtl-input"
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="editedItem.note"
                  label="ملاحظات"
                  class="rtl-input"
                  rows="3"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="showAddDialog = false">
            إلغاء
          </v-btn>
          <v-btn color="blue-darken-1" variant="text" @click="save" :disabled="!valid">
            حفظ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Print Receipt Template (hidden) -->
    <div id="printSection" style="display: none;">
      <div class="print-container">
        <!-- HEADER SECTION (30% height) -->
        <div class="receipt-header">
          <div class="logo-container">
            <img src="@/assets/soufara.png" alt="Logo" class="center-logo">
          </div>
          <h2 class="center-title">مركز سفراء العلم للتدريب</h2>
          <div class="receipt-subheader">
            <div class="receipt-date">{{ formatDate(new Date()) }}</div>
            <div class="receipt-type">إيصال دفع</div>
          </div>
        </div>
        
        <!-- DATA SECTION (40% height) -->
        <div class="receipt-data">
          <div class="receipt-row">
            <span class="receipt-label">اسم العميل:</span>
            <span class="receipt-value">{{ receiptData.customer }}</span>
          </div>
          <div class="receipt-row">
            <span class="receipt-label">الدورة:</span>
            <span class="receipt-value">{{ receiptData.courseName }}</span>
          </div>
          <div class="receipt-row">
            <span class="receipt-label">المبلغ الإجمالي:</span>
            <span class="receipt-value">{{ formatNumber(receiptData.totalPrice) }} د.ل</span>
          </div>
          <div class="receipt-row">
            <span class="receipt-label">المبلغ المدفوع:</span>
            <span class="receipt-value">{{ formatNumber(receiptData.amount) }} د.ل</span>
          </div>
          <div class="receipt-row">
            <span class="receipt-label">المتبقي قبل الدفع:</span>
            <span class="receipt-value">{{ formatNumber(receiptData.remaining_before) }} د.ل</span>
          </div>
          <div class="receipt-row">
            <span class="receipt-label">المتبقي بعد الدفع:</span>
            <span class="receipt-value">{{ formatNumber(receiptData.remaining_after) }} د.ل</span>
          </div>
          <div class="receipt-row" v-if="receiptData.note">
            <span class="receipt-label">ملاحظات:</span>
            <span class="receipt-value">{{ receiptData.note }}</span>
          </div>
        </div>
        
        <!-- FOOTER SECTION (30% height) -->
        <div class="receipt-footer">
          <div class="signature-section">
            <p class="signature-label">توقيع المستلم</p>
            <div class="signature-line"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Booking Confirmation Dialog -->
    <v-dialog v-model="showDeleteBookingDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">حذف الحجز</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف هذا الحجز؟ هذا الإجراء لا يمكن التراجع عنه.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="showDeleteBookingDialog = false">
            إلغاء
          </v-btn>
          <v-btn color="red-darken-1" variant="text" @click="deleteBookingConfirmed">
            حذف
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Booking Receipt Print Template (hidden) -->
    <div id="bookingPrintSection" style="display: none;">
      <div style="font-family: 'Cairo', sans-serif; direction: rtl; max-width: 600px; margin: 0 auto; padding: 20px; color: #000;">
        
        <!-- Header with Logo -->
        <div style="text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 12px;">
          <img src="@/assets/soufara.png" alt="Logo" style="max-width: 180px; margin-bottom: 8px;">
          <h2 style="font-size: 16px; margin: 0; font-weight: 600;">مركز سفراء العلم للتدريب</h2>
        </div>

        <!-- Receipt Type -->
        <div style="text-align: center; margin-bottom: 15px;">
          <h1 style="font-size: 20px; margin: 0; font-weight: bold;">حجز دورة</h1>
          <div style="font-size: 12px; margin-top: 3px;">التاريخ: {{ formatDate(bookingReceiptData.booking_date) }}</div>
        </div>

        <!-- Booking Details Table -->
        <table style="width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 13px;">
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold; width: 35%;">اسم العميل</td>
            <td style="padding: 6px 10px; text-align: right;">{{ bookingReceiptData.customer_name }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">الدورة</td>
            <td style="padding: 6px 10px; text-align: right;">{{ bookingReceiptData.course_name }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">من تاريخ</td>
            <td style="padding: 6px 10px; text-align: right;">{{ formatDate(bookingReceiptData.start_date) }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">إلى تاريخ</td>
            <td style="padding: 6px 10px; text-align: right;">{{ formatDate(bookingReceiptData.end_date) }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">عدد الأشخاص</td>
            <td style="padding: 6px 10px; text-align: right;">{{ bookingReceiptData.number_of_people }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">طريقة الدفع</td>
            <td style="padding: 6px 10px; text-align: right;">{{ bookingReceiptData.payment_type }}</td>
          </tr>
        </table>

        <!-- Financial Details -->
        <div style="margin: 15px 0; padding: 12px; border: 2px solid #000;">
          <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
            <tr>
              <td style="padding: 5px 0; font-weight: bold;">المبلغ الإجمالي</td>
              <td style="padding: 5px 0; text-align: left; font-weight: bold;">{{ bookingReceiptData.total_amount }} دينار ليبي</td>
            </tr>
            <tr style="border-top: 1px solid #ccc;">
              <td style="padding: 5px 0; font-weight: bold;">المبلغ المدفوع</td>
              <td style="padding: 5px 0; text-align: left;">{{ bookingReceiptData.paid_amount }} دينار ليبي</td>
            </tr>
            <tr style="border-top: 2px solid #000;">
              <td style="padding: 5px 0; font-weight: bold; font-size: 15px;">المبلغ المتبقي</td>
              <td style="padding: 5px 0; text-align: left; font-weight: bold; font-size: 15px;">{{ bookingReceiptData.remaining_amount }} دينار ليبي</td>
            </tr>
          </table>
        </div>

        <!-- Additional Info -->
        <div style="font-size: 12px; margin: 12px 0; padding: 10px 0; border-top: 1px solid #ddd;">
          <div style="margin-bottom: 5px;">
            <span style="font-weight: bold;">تم الحجز بواسطة:</span>
            <span style="margin-right: 8px;">{{ bookingReceiptData.booked_by }}</span>
          </div>
          <div>
            <span style="font-weight: bold;">تاريخ الحجز:</span>
            <span style="margin-right: 8px;">{{ formatDate(bookingReceiptData.booking_date) }}</span>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import paymentService from '../services/paymentService';
import courseReservationService from '../services/courseReservationService';

export default {
  name: 'CourseBooking',
  data() {
    return {
      search: '',
      bookings: [],
      showAddDialog: false,
      showDeleteDialog: false,
      paymentsDialog: false,
      addPaymentDialog: false,
      showDeletePaymentDialog: false,
      showDeleteBookingDialog: false,
      valid: false,
      editedIndex: -1,
      editedItem: {
        id: null,
        customer: '',
        course_id: null,
        number_of_people: 1,
        total_price: 0,
        discount: 0,
        payment_type: 'cash',
        status: 'reserved',
        note: ''
      },
      defaultItem: {
        id: null,
        customer: '',
        course_id: null,
        number_of_people: 1,
        total_price: 0,
        discount: 0,
        payment_type: 'cash',
        status: 'reserved',
        note: ''
      },
      selectedBooking: {
        id: null,
        customer: '',
        course_name: '',
        net_price: 0,
        remaining: 0
      },
      payments: [],
      newPayment: {
        course_reservation_id: null,
        amount: 0,
        note: ''
      },
      paymentToDelete: null,
      headers: [
        { title: 'العميل', key: 'customer' },
        { title: 'الدورة', key: 'course.name' },
        { title: 'التاريخ', key: 'date_range' },
        { title: 'الوقت', key: 'time_range' },
        { title: 'عدد الأشخاص', key: 'number_of_people' },
        { title: 'المبلغ الإجمالي', key: 'total_price' },
        { title: 'الخصم', key: 'discount' },
        { title: 'صافي المبلغ', key: 'net_price' },
        { title: 'المتبقي', key: 'remaining' },
        { title: 'الحالة', key: 'status' },
        { title: 'الإجراءات', key: 'actions', sortable: false }
      ],
      paymentHeaders: [
        { title: 'التاريخ', key: 'created_at', width: '20%' },
        { title: 'المبلغ', key: 'amount', width: '20%' },
        { title: 'المتبقي قبل', key: 'remaining_before', width: '20%' },
        { title: 'المتبقي بعد', key: 'remaining_after', width: '20%' },
        { title: 'ملاحظات', key: 'note' },
        { title: 'الإجراءات', key: 'actions', sortable: false }
      ],
      paymentTypes: [
        { text: 'كاش', value: 'cash' },
        { text: 'شيك', value: 'card' },
        { text: 'بطاقة ائتمان', value: 'credit_card' }
      ],
      statusOptions: [
        'reserved',
        'confirmed',
        'cancelled'
      ],
      selectedCoursePrice: 0,
      netAmount: 0,
      receiptData: {
        id: null,
        amount: 0,
        created_at: '',
        remaining_before: 0,
        remaining_after: 0,
        note: ''
      },
      amountInWords: '',
      bookingToDelete: null,
      bookingReceiptData: {
        customer_name: '',
        course_name: '',
        start_date: '',
        end_date: '',
        number_of_people: 0,
        payment_type: '',
        total_amount: 0,
        paid_amount: 0,
        remaining_amount: 0,
        booked_by: '',
        booking_date: ''
      }
    };
  },

  methods: {
    ...mapActions('course', ['fetchCourses']),

    async fetchBookings() {
      try {
        const response = await courseReservationService.getReservations();
        console.log('Bookings response:', response.data);
        this.bookings = response.data.data || [];
        
        if (response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
          this.bookings = response.data.data.data;
        }
        
        if (!Array.isArray(this.bookings)) {
          console.error('Bookings is not an array:', this.bookings);
          this.bookings = [];
        }
      } catch (error) {
        console.error('Error fetching bookings:', error);
        this.bookings = [];
      }
    },

    editItem(item) {
      this.editedIndex = this.bookings.indexOf(item);
      this.editedItem = Object.assign({}, item);
      if (item.course && item.course.price_per_person) {
        this.selectedCoursePrice = item.course.price_per_person;
      }
      this.showAddDialog = true;
    },

    deleteItem(item) {
      this.editedIndex = this.bookings.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.showDeleteDialog = true;
    },

    async confirmDelete() {
      try {
        await courseReservationService.deleteReservation(this.editedItem.id);
        this.showDeleteDialog = false;
        this.fetchBookings();
      } catch (error) {
        console.error('Error deleting booking:', error);
      }
    },

    async save() {
      if (!this.$refs.form.validate()) {
        return;
      }
      
      try {
        if (this.editedItem.id) {
          await courseReservationService.updateReservation(this.editedItem.id, this.editedItem);
        } else {
          const bookingData = { ...this.editedItem };
          delete bookingData.id;
          
          await courseReservationService.createReservation(bookingData);
        }
        
        this.showAddDialog = false;
        this.fetchBookings();
      } catch (error) {
        console.error('Error saving booking:', error);
      }
    },

    resetForm() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.selectedCoursePrice = 0;
      this.netAmount = 0;
      this.$refs.form?.reset();
    },

    showPayments(item) {
      this.selectedBooking = {
        id: item.id,
        customer: item.customer,
        course_name: item.course?.name || '',
        net_price: Number(item.net_price) || (Number(item.total_price) - Number(item.discount || 0)),
        remaining: item.remaining
      };

      this.newPayment = {
        course_reservation_id: item.id,
        amount: 0,
        note: '',
      };
      
      this.fetchPayments();
      this.paymentsDialog = true;
    },

    showAddPayment() {
      this.newPayment.amount = this.selectedBooking.remaining;
      this.addPaymentDialog = true;
    },

    async fetchPayments() {
      try {
        const response = await paymentService.getPayments({
          paymentable_id: this.selectedBooking.id,
          type: 'course'
        });
        
        // Handle the response based on its structure
        let paymentsData = [];
        
        if (response.data && Array.isArray(response.data)) {
          paymentsData = response.data;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          paymentsData = response.data.data;
        }
        
        this.payments = paymentsData;
      } catch (error) {
        console.error('Error fetching payments:', error);
        this.payments = [];
      }
    },

    async savePayment() {
      try {
        if (!this.$refs.paymentForm.validate()) {
          return;
        }
        
        await paymentService.createPayment({
          ...this.newPayment,
          amount: parseFloat(this.newPayment.amount)
        });
        
        this.addPaymentDialog = false;
        await this.fetchPayments();
        await this.fetchBookings();
      } catch (error) {
        console.error('Error saving payment:', error);
      }
    },

    async confirmDeletePayment(payment) {
      this.paymentToDelete = payment;
      this.showDeletePaymentDialog = true;
    },
    
    async deletePaymentConfirmed() {
      try {
        if (!this.paymentToDelete || !this.paymentToDelete.id) {
          console.error('No payment selected for deletion');
          return;
        }
        
        await paymentService.deletePayment(this.paymentToDelete.id);
        
        // Wait for both operations to complete
        await Promise.all([
          this.fetchPayments(),
          this.fetchBookings()
        ]);
        
        this.showDeletePaymentDialog = false;
        this.paymentToDelete = null;
        
        // Show success message
        this.$store.dispatch('showAlert', {
          message: 'تم حذف الدفعة بنجاح',
          type: 'success'
        });
      } catch (error) {
        console.error('Error deleting payment:', error);
        this.$store.dispatch('showAlert', {
          message: 'حدث خطأ أثناء حذف الدفعة',
          type: 'error'
        });
      }
    },

    async printReceipt(payment) {
      try {
        // Make sure selectedBooking is loaded
        if (!this.selectedBooking || !this.selectedBooking.id) {
          console.error('No booking selected for receipt printing');
          return;
        }
        
        // Store the course name and total price before printing
        const courseName = this.selectedBooking.course_name;
        const totalPrice = this.selectedBooking.net_price;
        
        console.log('Printing receipt with course:', courseName, 'and total:', totalPrice);
        
        this.receiptData = {
          id: payment.id,
          amount: payment.amount,
          created_at: payment.created_at,
          remaining_before: payment.remaining_before,
          remaining_after: payment.remaining_after,
          note: payment.note || '',
          customer: this.selectedBooking.customer,
          courseName: courseName,
          totalPrice: totalPrice
        };
        
        // Wait for the next DOM update cycle
        await this.$nextTick();
        
        // Print the receipt
        const printContent = document.getElementById('printSection');
        const originalContents = document.body.innerHTML;
        
        document.body.innerHTML = printContent.innerHTML;
        document.body.style.direction = 'rtl';
        document.body.style.fontFamily = "'Cairo', sans-serif";
        
        window.print();
        document.body.innerHTML = originalContents;
        
        // Reload the Vue instance
        location.reload();
      } catch (error) {
        console.error('Error printing receipt:', error);
      }
    },

    convertNumberToArabicWords(number) {
      // Simple implementation for now
      const amount = parseFloat(number);
      return amount.toFixed(2) + ' دينار ليبي';
    },

    formatDateRange(dateFrom, dateTo) {
      if (!dateFrom || !dateTo) return '';
      return `${dateFrom} - ${dateTo}`;
    },

    formatTimeRange(timeFrom, timeTo) {
      if (!timeFrom || !timeTo) return '';
      return `${timeFrom} - ${timeTo}`;
    },
    
    formatNumber(number) {
      return Number(number).toLocaleString('ar-LY');
    },
    
    getStatusColor(status) {
      switch (status) {
        case 'confirmed':
          return 'success';
        case 'reserved':
          return 'warning';
        case 'cancelled':
          return 'error';
        default:
          return 'grey';
      }
    },
    
    getStatusText(status) {
      switch (status) {
        case 'confirmed':
          return 'مؤكد';
        case 'reserved':
          return 'محجوز';
        case 'cancelled':
          return 'ملغي';
        default:
          return status;
      }
    },
    
    getRemainingCellStyle(remaining) {
      const amount = Number(remaining) || 0;
      if (amount > 0) {
        return {
          backgroundColor: '#ffebee',
          padding: '16px',
          margin: '-16px',
          fontWeight: 'bold',
          textAlign: 'center'
        };
      } else {
        return {
          backgroundColor: '#e8f5e9',
          padding: '16px',
          margin: '-16px',
          fontWeight: 'bold',
          textAlign: 'center'
        };
      }
    },
    
    updateCourseSelection() {
      console.log('Course selected:', this.editedItem.course_id);
      const selectedCourse = this.courses.find(course => course.id === this.editedItem.course_id);
      
      if (selectedCourse) {
        console.log('Selected course:', selectedCourse);
        this.selectedCoursePrice = selectedCourse.price_per_person || 0;
        console.log('Selected course price:', this.selectedCoursePrice);
        this.calculateTotalPrice();
      } else {
        this.selectedCoursePrice = 0;
        this.editedItem.total_price = 0;
        this.netAmount = 0;
      }
    },
    
    calculateTotalPrice() {
      console.log('Calculating total price...');
      console.log('Number of people:', this.editedItem.number_of_people);
      console.log('Price per person:', this.selectedCoursePrice);
      
      const numberOfPeople = parseInt(this.editedItem.number_of_people) || 1;
      const pricePerPerson = parseFloat(this.selectedCoursePrice) || 0;
      this.editedItem.total_price = numberOfPeople * pricePerPerson;
      
      console.log('Calculated total price:', this.editedItem.total_price);
      
      this.calculateNetAmount();
    },
    
    calculateNetAmount() {
      console.log('Calculating net amount...');
      const totalPrice = parseFloat(this.editedItem.total_price) || 0;
      const discount = parseFloat(this.editedItem.discount) || 0;
      this.netAmount = totalPrice - discount;
    //  this.editedItem.net_price = this.netAmount;
      
      console.log('Net amount:', this.netAmount);
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('ar-LY');
    },

    async confirmDeleteBooking(id) {
      this.bookingToDelete = id;
      this.showDeleteBookingDialog = true;
    },

    async deleteBookingConfirmed() {
      try {
        if (!this.bookingToDelete) {
          console.error('No booking selected for deletion');
          return;
        }
        
        await courseReservationService.deleteReservation(this.bookingToDelete);
        await this.fetchBookings();
        
        this.showDeleteBookingDialog = false;
        this.bookingToDelete = null;
        
        // Show success message
        this.$store.dispatch('showAlert', {
          message: 'تم حذف الحجز بنجاح',
          type: 'success'
        });
      } catch (error) {
        console.error('Error deleting booking:', error);
        this.$store.dispatch('showAlert', {
          message: 'حدث خطأ أثناء حذف الحجز',
          type: 'error'
        });
      }
    },

    async printBookingReceipt(booking) {
      try {
        // Get course details
        const courseName = booking.course?.name || 'غير معروف';
        
        // Calculate paid and remaining amounts
        const totalAmount = booking.total_price - (booking.discount || 0);
        const paidAmount = totalAmount - (booking.remaining || 0);
        const remainingAmount = booking.remaining || 0;
        
        // Get booked by (username from auth or created_by field)
        const user = this.$store.state.auth.user;
        const bookedBy = booking.created_by_name || user?.name || 'غير معروف';
        
        // Get start and end dates from course
        const startDate = booking.course?.date_from || new Date().toISOString();
        const endDate = booking.course?.date_to || new Date().toISOString();
        
        // Get payment type text
        const paymentTypeObj = this.paymentTypes.find(pt => pt.value === booking.payment_type);
        const paymentTypeText = paymentTypeObj ? paymentTypeObj.text : booking.payment_type || 'غير محدد';
        
        // Prepare receipt data
        this.bookingReceiptData = {
          customer_name: booking.customer,
          course_name: courseName,
          start_date: startDate,
          end_date: endDate,
          number_of_people: booking.number_of_people || 1,
          payment_type: paymentTypeText,
          total_amount: totalAmount,
          paid_amount: paidAmount,
          remaining_amount: remainingAmount,
          booked_by: bookedBy,
          booking_date: booking.created_at || new Date().toISOString()
        };
        
        // Wait for next tick to ensure DOM is updated
        await this.$nextTick();
        
        // Create print window
        const printContent = document.getElementById('bookingPrintSection').innerHTML;
        const printWindow = window.open('', '_blank');
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html dir="rtl">
            <head>
              <title>إيصال حجز دورة</title>
              <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap" rel="stylesheet">
              <style>
                @page {
                  size: A4;
                  margin: 1cm;
                }
                body {
                  font-family: 'Cairo', sans-serif;
                  direction: rtl;
                  margin: 0;
                  padding: 0;
                }
                @media print {
                  body {
                    print-color-adjust: exact;
                    -webkit-print-color-adjust: exact;
                  }
                }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `);
        
        printWindow.document.close();
        
        // Wait for images to load before printing
        printWindow.onload = function() {
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            printWindow.onafterprint = function() {
              printWindow.close();
            };
          }, 250);
        };
      } catch (error) {
        console.error('Error printing booking receipt:', error);
        this.$store.dispatch('showAlert', {
          message: 'حدث خطأ أثناء طباعة الإيصال',
          type: 'error'
        });
      }
    }
  },

  watch: {
    showAddDialog(val) {
      if (!val) {
        this.resetForm();
      } else {
        if (this.editedItem.course_id) {
          this.updateCourseSelection();
        }
      }
    },
    paymentsDialog(val) {
      if (!val) {
        this.payments = [];
      }
    },
    showDeleteDialog(val) {
      if (!val) {
        this.resetForm();
      }
    },
    showDeletePaymentDialog(val) {
      if (!val) {
        this.paymentToDelete = null;
      }
    },
    'editedItem.discount'() {
      this.calculateNetAmount();
    }
  },

  async created() {
    this.bookings = [];
    this.payments = [];
    
    await this.fetchCourses();
    await this.fetchBookings();
    if (this.$route.query.showAdd === 'true') {
      this.showAddDialog = true;
    }
  },

  computed: {
    ...mapState({
      courses: state => state.course.courses,
      loading: state => state.loading
    }),
    ...mapGetters('auth', ['hasPermission']),
    hasDeletePermission() {
      // Check for the correct permission name or admin role
      const user = this.$store.state.auth.user;
      const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
      
      return this.hasPermission('admin.course-reservations.destroy') || isAdmin;
    }
  }
}
</script>

<!-- CourseBooking specific styles only -->
<style scoped>
.rtl-table {
  direction: rtl;
  width: 100%;
  margin-bottom: 20px;
}

td.text-center {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.main-table {
  margin-top: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.payment-dialog, .booking-dialog {
  padding: 20px;
  max-width: 900px;
  margin: 0 auto;
}

.summary-row {
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
  margin-bottom: 20px;
}

/* Receipt printing styles */
#printSection {
  direction: rtl;
  font-family: 'Cairo', sans-serif;
}

.print-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.receipt-header {
  height: 30vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.center-logo {
  width: 40%;
  max-height: 15vh;
  object-fit: contain;
}

.center-title {
  font-family: 'Cairo', sans-serif;
  font-size: 24px;
  margin: 15px 0;
  text-align: center;
}

.receipt-subheader {
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 10%;
  margin-top: 20px;
}

.receipt-type {
  font-family: 'Cairo', sans-serif;
  font-size: 18px;
  font-weight: bold;
}

.receipt-date {
  font-family: 'Cairo', sans-serif;
  font-size: 18px;
}

.receipt-data {
  height: 40vh;
  padding: 20px 10%;
  overflow-y: auto;
}

.receipt-row {
  display: flex;
  margin-bottom: 15px;
  font-family: 'Cairo', sans-serif;
}

.receipt-label {
  font-weight: bold;
  width: 150px;
  text-align: right;
  font-family: 'Cairo', sans-serif;
}

.receipt-value {
  flex: 1;
  text-align: right;
  font-family: 'Cairo', sans-serif;
}

.receipt-footer {
  height: 30vh;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 10%;
}

.signature-section {
  text-align: center;
  margin-right: 20px;
}

.signature-label {
  font-family: 'Cairo', sans-serif;
  font-size: 18px;
  margin-bottom: 30px;
}

.signature-line {
  width: 200px;
  border-bottom: 1px solid #000;
}

@media print {
  body, html {
    margin: 0;
    padding: 0;
    height: 100%;
    direction: rtl;
    font-family: 'Cairo', sans-serif !important;
  }
  
  .print-container {
    height: 100%;
    page-break-inside: avoid;
  }
}
</style> 